import React, { useEffect, useState } from 'react';
import {
  Mail, Phone, MessageCircle, Settings, Save, Loader2, Eye, EyeOff,
  CheckCircle2, XCircle, AlertCircle, Send, ExternalLink,
} from 'lucide-react';
import { AdminAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const ICONS = {
  resend: Mail,
  twilio_sms: Phone,
  twilio_whatsapp: MessageCircle,
  general: Settings,
};

const HELP_LINKS = {
  resend: { url: 'https://resend.com/api-keys', label: 'resend.com → API Keys' },
  twilio_sms: { url: 'https://console.twilio.com', label: 'console.twilio.com' },
  twilio_whatsapp: { url: 'https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn', label: 'Twilio WhatsApp Sandbox' },
  general: null,
};

const TEST_PLACEHOLDERS = {
  resend: 'email-de-test@exemple.com',
  twilio_sms: '+213 5XX XX XX XX',
  twilio_whatsapp: '+213 5XX XX XX XX',
};

const SecretField = ({ value, onChange, placeholder, testid }) => {
  const [show, setShow] = useState(false);
  const isMasked = value && value.includes('•');
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`pr-10 font-mono text-sm ${isMasked ? 'text-[#7A6F5C]' : ''}`}
        data-testid={testid}
      />
      <button type="button" onClick={() => setShow(!show)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7A6F5C] hover:text-[#1F4D3F]"
        aria-label={show ? 'Masquer' : 'Afficher'}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const IntegrationCard = ({ id, group, values, status, onSave, onTest, saving, testing }) => {
  const Icon = ICONS[id] || Settings;
  const help = HELP_LINKS[id];
  const [formValues, setFormValues] = useState(values);
  const [testTo, setTestTo] = useState('');

  useEffect(() => { setFormValues(values); }, [values]);

  const setField = (key, val) => setFormValues((p) => ({ ...p, [key]: val }));

  const dirty = Object.keys(group.fields).some((k) => (formValues[k] || '') !== (values[k] || ''));
  const canTest = id !== 'general' && status;

  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5" data-testid={`integration-card-${id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${status ? 'bg-emerald-50 text-emerald-600' : 'bg-[#FAF7F2] text-[#7A6F5C]'}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-medium text-[#2D2A26]">{group.label}</h3>
            <div className="flex items-center gap-1.5 mt-1 text-xs">
              {status ? (
                <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> <span className="text-emerald-700">Configuré</span></>
              ) : (
                <><XCircle className="w-3.5 h-3.5 text-rose-500" /> <span className="text-rose-600">Non configuré</span></>
              )}
              {help && (
                <a href={help.url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-[#1F4D3F] hover:underline">
                  {help.label} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(group.fields).map(([key, meta]) => (
          <div key={key}>
            <label className="text-xs font-medium text-[#2D2A26] mb-1 block">{meta.label}</label>
            {meta.type === 'bool' ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(formValues[key] || 'false').toLowerCase() === 'true'}
                  onChange={(e) => setField(key, e.target.checked ? 'true' : 'false')}
                  className="w-4 h-4 rounded accent-[#1F4D3F]"
                  data-testid={`integration-field-${key}`}
                />
                <span className="text-sm text-[#5A5247]">{(formValues[key] || 'false').toLowerCase() === 'true' ? 'Activé' : 'Désactivé'}</span>
              </label>
            ) : meta.secret ? (
              <SecretField
                value={formValues[key] || ''}
                onChange={(v) => setField(key, v)}
                placeholder={meta.placeholder}
                testid={`integration-field-${key}`}
              />
            ) : (
              <Input
                value={formValues[key] || ''}
                onChange={(e) => setField(key, e.target.value)}
                placeholder={meta.placeholder || meta.default || ''}
                className="text-sm"
                data-testid={`integration-field-${key}`}
              />
            )}
            {meta.secret && formValues[key] && formValues[key].includes('•') && (
              <p className="text-[10px] text-[#7A6F5C] mt-1">Valeur masquée — videz le champ et recollez pour mettre à jour.</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-5 pt-4 border-t border-[#F5F1EA]">
        {canTest ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={TEST_PLACEHOLDERS[id]}
              className="text-xs h-9 max-w-[220px]"
              data-testid={`integration-test-to-${id}`}
            />
            <Button size="sm" variant="outline" onClick={() => onTest(id, testTo)} disabled={testing || !testTo}
              className="border-[#E8E0D0] text-[#1F4D3F] hover:bg-[#FAF7F2]"
              data-testid={`integration-test-${id}`}>
              {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Tester
            </Button>
          </div>
        ) : <div />}
        <Button size="sm" onClick={() => onSave(id, formValues)} disabled={!dirty || saving}
          className="bg-[#1F4D3F] hover:bg-[#173B30] text-white"
          data-testid={`integration-save-${id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

const IntegrationsTab = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await AdminAPI.getIntegrations()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (id, values) => {
    setSaving(id);
    try {
      await AdminAPI.saveIntegration(id, values);
      toast({ title: '✅ Enregistré', description: `${data.schema[id].label} mis à jour.` });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(null); }
  };

  const onTest = async (id, testTo) => {
    setTesting(id);
    try {
      const res = await AdminAPI.testIntegration(id, testTo);
      if (res.ok) {
        toast({ title: '✅ Test réussi', description: 'Vérifiez votre boîte de réception / téléphone.' });
      } else {
        toast({ title: '⚠️ Test échoué', description: res.reason || res.error || 'Vérifiez la configuration.' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec du test' });
    } finally { setTesting(null); }
  };

  if (loading) {
    return <div className="text-center py-10 text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>;
  }
  if (!data) return null;

  return (
    <div data-testid="admin-integrations-tab" className="space-y-4">
      <div className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-2xl p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-[#1F4D3F] shrink-0 mt-0.5" />
        <div className="text-sm text-[#5A5247]">
          <p className="font-medium text-[#2D2A26]">Configurez vos clés API directement depuis cette interface.</p>
          <p className="text-xs mt-1">
            Les clés sensibles sont stockées de manière sécurisée et masquées à l'affichage.
            Cliquez sur <strong>Tester</strong> pour vérifier qu'une intégration fonctionne avant de l'utiliser en production.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(data.schema).map(([id, group]) => {
          const groupValues = {};
          Object.keys(group.fields).forEach((k) => { groupValues[k] = data.values[k] || ''; });
          return (
            <IntegrationCard
              key={id}
              id={id}
              group={group}
              values={groupValues}
              status={data.status[id]}
              onSave={onSave}
              onTest={onTest}
              saving={saving === id}
              testing={testing === id}
            />
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationsTab;
